import CodecParser, { OggPage, OpusHeader } from "codec-parser"

interface SeekablePage {
  granulePosition: bigint
  pageIndex: number
  startGranulePosition: bigint
}

/**
 * Returns true when the page has a real granule position and can therefore be
 * used as a seek anchor.
 */
function isSeekablePage(page: OggPage): boolean {
  return BigInt(page.absoluteGranulePosition) >= 0n
}

/**
 * Parsed, seekable view of a single Ogg/Opus region.
 *
 * This class does all container-level work once up front: parsing Ogg pages,
 * validating the Opus header, computing region duration, and building a compact
 * list of pages that can be used as seek anchors. `OpusRegionDecoder` then uses
 * this index to jump into the stream without reparsing the file on every seek.
 */
export class OggOpusIndex {
  readonly channels: number
  readonly durationSeconds: number
  readonly header: OpusHeader
  readonly pages: OggPage[]
  readonly preSkip: number

  private readonly seekablePages: SeekablePage[]
  private readonly seekablePagesByIndex: Map<number, SeekablePage>

  /**
   * Parses the full Ogg/Opus payload and extracts the metadata needed by the
   * runtime decoder.
   */
  constructor(oggOpusData: Uint8Array) {
    const codecParser = new CodecParser<OggPage>("application/ogg", {
      enableFrameCRC32: false,
      onCodec: (codec) => {
        if (codec !== "opus") {
          throw new Error(`Unsupported Ogg codec ${codec}`)
        }
      },
    })

    const parsedPages = [
      ...(codecParser.parseChunk(oggOpusData) as unknown as Iterable<OggPage>),
      ...(codecParser.flush() as unknown as Iterable<OggPage>),
    ]

    if (parsedPages.length === 0) {
      throw new Error("No Ogg pages found")
    }

    const firstAudioPage = parsedPages.find((page) => page.codecFrames.length > 0)
    const header = firstAudioPage?.codecFrames[0]?.header as OpusHeader | undefined

    if (!header || typeof header.preSkip !== "number" || typeof header.channels !== "number") {
      throw new Error("Opus identification header not found")
    }

    // RFC 8486 Section 5.2 tightens the rules around channel mapping families.
    // We only support mono/stereo tracks in the streaming backend today.
    if (header.channels < 1 || header.channels > 2) {
      throw new Error(`Unsupported Opus channel count ${header.channels}`)
    }

    this.header = header
    this.channels = header.channels
    this.preSkip = header.preSkip
    this.pages = parsedPages
    let previousSeekableGranulePosition: bigint | null = null
    this.seekablePages = parsedPages.flatMap((page, pageIndex) => {
      if (!isSeekablePage(page) || page.codecFrames.length === 0) {
        return []
      }

      const granulePosition = BigInt(page.absoluteGranulePosition)
      const firstPageStartGranulePosition = granulePosition - BigInt(page.samples)
      const startGranulePosition = previousSeekableGranulePosition ??
        (firstPageStartGranulePosition > 0n ? firstPageStartGranulePosition : 0n)
      const seekablePage = { granulePosition, pageIndex, startGranulePosition }
      previousSeekableGranulePosition = granulePosition
      return [seekablePage]
    })
    this.seekablePagesByIndex = new Map(
      this.seekablePages.map((page) => [page.pageIndex, page])
    )

    const lastSeekablePage = this.seekablePages.at(-1)
    if (!lastSeekablePage) {
      throw new Error("Opus stream has no seekable pages")
    }

    // RFC 7845 Section 4.3: PCM position is derived from granule position
    // by subtracting the stream pre-skip, still in 48 kHz sample units.
    const pcmSamples = lastSeekablePage.granulePosition - BigInt(this.preSkip)
    this.durationSeconds = Math.max(0, Number(pcmSamples)) / 48000
  }

  /**
   * Finds the earliest seekable page whose granule position is at or after the
   * requested granule.
   *
   * The decoder uses this as its coarse seek anchor, then decodes forward with
   * preroll to land on the exact audible target.
   */
  findPageIndexAtOrAfterGranule(targetGranule: bigint): number {
    let left = 0
    let right = this.seekablePages.length - 1
    let matchIndex = this.seekablePages.length

    while (left <= right) {
      const middle = Math.floor((left + right) / 2)
      const page = this.seekablePages[middle]

      if (page.granulePosition >= targetGranule) {
        matchIndex = middle
        right = middle - 1
      } else {
        left = middle + 1
      }
    }

    if (matchIndex === this.seekablePages.length) {
      return this.pages.length
    }

    let pageIndex = this.seekablePages[matchIndex].pageIndex

    // RFC 7845 Section 4: a page that only carries packet continuation bytes can
    // have a granule position of -1, so we walk backwards to the first page that
    // belongs to the packet chain we need to decode from.
    while (pageIndex > 0 && this.pages[pageIndex].isContinuedPacket) {
      pageIndex--
    }

    return pageIndex
  }

  /**
   * Returns the raw parsed Ogg page by index so the decoder can pull packet data.
   */
  getPage(index: number): OggPage | undefined {
    return this.pages[index]
  }

  /**
   * Returns the indexed timing span for a seekable page.
   *
   * The start granule is tracked independently from `page.samples` so packet
   * continuation pages with `-1` granules do not skew seek math on the next
   * seekable page.
   */
  getSeekablePageInfo(index: number): SeekablePage | null {
    return this.seekablePagesByIndex.get(index) ?? null
  }
}
